const TAG_NAME_MAP: Record<string, string> = {
  B: 'strong',
  STRONG: 'strong',
  I: 'em',
  EM: 'em',
  P: 'p',
  DIV: 'p',
  BR: 'br',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  A: 'a',
};

const EMPTY_HTML_PATTERN = /^(?:\s|&nbsp;|<br\s*\/?>|<p><br\s*\/?><\/p>|<p>\s*<\/p>)*$/i;

function escapeHtml(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function hasMarkup(value: string): boolean {
  return /<[^>]+>/.test(value);
}

function sanitizeHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;

  try {
    const parsed = new URL(trimmed, window.location.origin);
    const protocol = parsed.protocol.toLowerCase();
    if (protocol === 'http:' || protocol === 'https:' || protocol === 'mailto:' || protocol === 'tel:') {
      return parsed.href;
    }
  } catch {
    return null;
  }

  return null;
}

function sanitizeNode(node: Node, documentRef: Document): Node[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [documentRef.createTextNode(node.textContent || '')];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const source = node as HTMLElement;
  const mappedTag = TAG_NAME_MAP[source.tagName];
  const childNodes = Array.from(source.childNodes).flatMap((child) => sanitizeNode(child, documentRef));

  if (!mappedTag) {
    return childNodes;
  }

  const element = documentRef.createElement(mappedTag);
  childNodes.forEach((child) => element.appendChild(child));

  if (mappedTag === 'a') {
    const sanitizedHref = sanitizeHref(source.getAttribute('href') || '');
    if (!sanitizedHref) {
      return childNodes;
    }

    element.setAttribute('href', sanitizedHref);
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer nofollow');
  }

  return [element];
}

function normalizePlainText(value: string): string {
  return value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function prepareRichTextInput(value?: string | null): string {
  const trimmed = value?.trim() || '';
  if (!trimmed) return '';
  return hasMarkup(trimmed) ? sanitizeRichText(trimmed) : normalizePlainText(trimmed);
}

export function sanitizeRichText(value?: string | null): string {
  const trimmed = value?.trim() || '';
  if (!trimmed) return '';

  const template = document.createElement('template');
  template.innerHTML = trimmed;

  const container = document.createElement('div');
  Array.from(template.content.childNodes)
    .flatMap((child) => sanitizeNode(child, document))
    .forEach((child) => container.appendChild(child));

  const sanitized = container.innerHTML.trim();
  return EMPTY_HTML_PATTERN.test(sanitized) ? '' : sanitized;
}

export function renderRichText(value?: string | null): string {
  const sanitized = prepareRichTextInput(value);
  if (!sanitized) return '';
  return `<div class="rich-text-content">${sanitized}</div>`;
}
